/**
 * Secure Storage Service
 * 
 * Uses localStorage for tokens and user data with 7-day expiration.
 * Session persists across browser restarts for 7 days.
 * 
 * Security considerations:
 * - Tokens are stored in localStorage with expiration tracking
 * - Expired sessions are automatically cleared
 * - Device ID is stored separately in localStorage
 */

class SecureStorageService {
  // Local storage keys (persistent with expiration)
  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly USER_DATA_KEY = 'user_data'; // Minimal user data (non-sensitive)
  private readonly SESSION_EXPIRES_AT_KEY = 'session_expires_at'; // Timestamp when session expires
  
  // Local storage keys (persistent, but non-sensitive)
  private readonly DEVICE_ID_KEY = 'web_device_id';
  
  // Session duration: 7 days in milliseconds
  private readonly SESSION_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

  /**
   * Check if the current session has expired
   */
  isSessionExpired(): boolean {
    try {
      const expiresAt = localStorage.getItem(this.SESSION_EXPIRES_AT_KEY);
      if (!expiresAt) {
        return true; // No expiration timestamp means session is invalid
      }
      const expirationTime = parseInt(expiresAt, 10);
      return Date.now() >= expirationTime;
    } catch (error) {
      console.error('Error checking session expiration:', error);
      return true; // Assume expired on error
    }
  }

  /**
   * Set session expiration timestamp (7 days from now)
   * Uses rolling expiration - extends session if it's about to expire soon
   */
  private setSessionExpiration(forceReset: boolean = false): void {
    try {
      const now = Date.now();
      const newExpiration = now + this.SESSION_DURATION_MS;
      
      if (forceReset) {
        // Force reset to 7 days from now (e.g., on initial login)
        localStorage.setItem(this.SESSION_EXPIRES_AT_KEY, newExpiration.toString());
        return;
      }
      
      // Check current expiration
      const currentExpiresAt = localStorage.getItem(this.SESSION_EXPIRES_AT_KEY);
      if (!currentExpiresAt) {
        // No expiration set, set it to 7 days from now
        localStorage.setItem(this.SESSION_EXPIRES_AT_KEY, newExpiration.toString());
        return;
      }
      
      const currentExpiration = parseInt(currentExpiresAt, 10);
      const timeUntilExpiration = currentExpiration - now;
      
      // If expiration is less than 1 day away, extend it to 7 days from now (rolling expiration)
      // This keeps active users logged in while allowing inactive sessions to expire
      const oneDayMs = 24 * 60 * 60 * 1000;
      if (timeUntilExpiration < oneDayMs) {
        localStorage.setItem(this.SESSION_EXPIRES_AT_KEY, newExpiration.toString());
      }
      // Otherwise, keep the existing expiration (don't extend indefinitely)
    } catch (error) {
      console.error('Error setting session expiration:', error);
    }
  }

  /**
   * Get access token from localStorage (if session not expired)
   */
  getAccessToken(): string | null {
    try {
      // Check if session is expired
      if (this.isSessionExpired()) {
        this.clearTokens(); // Auto-cleanup expired session
        return null;
      }
      return localStorage.getItem(this.ACCESS_TOKEN_KEY);
    } catch (error) {
      console.error('Error reading access token from localStorage:', error);
      return null;
    }
  }

  /**
   * Set access token in localStorage and update expiration
   * @param token The access token to store
   * @param forceResetExpiration If true, reset expiration to 7 days from now (e.g., on initial login)
   */
  setAccessToken(token: string | null, forceResetExpiration: boolean = false): void {
    try {
      if (token) {
        localStorage.setItem(this.ACCESS_TOKEN_KEY, token);
        this.setSessionExpiration(forceResetExpiration); // Update expiration when token is set
      } else {
        localStorage.removeItem(this.ACCESS_TOKEN_KEY);
        localStorage.removeItem(this.SESSION_EXPIRES_AT_KEY);
      }
    } catch (error) {
      console.error('Error writing access token to localStorage:', error);
    }
  }

  /**
   * Get refresh token from localStorage (if session not expired)
   */
  getRefreshToken(): string | null {
    try {
      // Check if session is expired
      if (this.isSessionExpired()) {
        this.clearTokens(); // Auto-cleanup expired session
        return null;
      }
      return localStorage.getItem(this.REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error('Error reading refresh token from localStorage:', error);
      return null;
    }
  }

  /**
   * Set refresh token in localStorage and update expiration
   * @param token The refresh token to store
   * @param forceResetExpiration If true, reset expiration to 7 days from now (e.g., on initial login)
   */
  setRefreshToken(token: string | null, forceResetExpiration: boolean = false): void {
    try {
      if (token) {
        localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
        this.setSessionExpiration(forceResetExpiration); // Update expiration when token is set
      } else {
        localStorage.removeItem(this.REFRESH_TOKEN_KEY);
        localStorage.removeItem(this.SESSION_EXPIRES_AT_KEY);
      }
    } catch (error) {
      console.error('Error writing refresh token to localStorage:', error);
    }
  }

  /**
   * Set user data in localStorage (minimal, non-sensitive data only)
   * @param user The user data to store
   * @param forceResetExpiration If true, reset expiration to 7 days from now (e.g., on initial login)
   */
  setUserData(user: any, forceResetExpiration: boolean = false): void {
    try {
      if (user) {
        // Store only non-sensitive user data
        const safeUserData = {
          id: user.id,
          username: user.username,
          email: user.email,
          photoUrl: user.photoUrl,
          isActive: user.isActive
        };
        localStorage.setItem(this.USER_DATA_KEY, JSON.stringify(safeUserData));
        this.setSessionExpiration(forceResetExpiration); // Update expiration when user data is set
      } else {
        localStorage.removeItem(this.USER_DATA_KEY);
        localStorage.removeItem(this.SESSION_EXPIRES_AT_KEY);
      }
    } catch (error) {
      console.error('Error writing user data to localStorage:', error);
    }
  }

  /**
   * Get user data from localStorage (if session not expired)
   */
  getUserData(): any | null {
    try {
      // Check if session is expired
      if (this.isSessionExpired()) {
        this.clearTokens(); // Auto-cleanup expired session
        return null;
      }
      const userData = localStorage.getItem(this.USER_DATA_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error reading user data from localStorage:', error);
      return null;
    }
  }

  /**
   * Clear all tokens and user data (localStorage)
   */
  clearTokens(): void {
    try {
      localStorage.removeItem(this.ACCESS_TOKEN_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      localStorage.removeItem(this.USER_DATA_KEY);
      localStorage.removeItem(this.SESSION_EXPIRES_AT_KEY);
    } catch (error) {
      console.error('Error clearing tokens from localStorage:', error);
    }
  }

  /**
   * Get device ID from localStorage (non-sensitive, can persist)
   */
  getDeviceId(): string | null {
    try {
      return localStorage.getItem(this.DEVICE_ID_KEY);
    } catch (error) {
      console.error('Error reading device ID from localStorage:', error);
      return null;
    }
  }

  /**
   * Set device ID in localStorage (non-sensitive, can persist)
   */
  setDeviceId(deviceId: string): void {
    try {
      localStorage.setItem(this.DEVICE_ID_KEY, deviceId);
    } catch (error) {
      console.error('Error writing device ID to localStorage:', error);
    }
  }

  /**
   * Clear device ID (for logout/device deletion)
   */
  clearDeviceId(): void {
    try {
      localStorage.removeItem(this.DEVICE_ID_KEY);
    } catch (error) {
      console.error('Error clearing device ID from localStorage:', error);
    }
  }

  /**
   * Clear all storage (both sessionStorage and localStorage)
   */
  clearAll(): void {
    this.clearTokens();
    this.clearDeviceId();
  }
}

export const secureStorage = new SecureStorageService();

